/*
 * SPDX-FileCopyrightText: © 2024 Schneider Electric
 *
 * SPDX-License-Identifier: Apache-2.0
 */

@import url('../shared.css');

:host {
  display: inline-block;
  line-height: 0;
}

[aria-disabled='true'] {
  opacity: var(--qds-theme-disabled);
  pointer-events: none;
}

.qds-target {
  appearance: none;
  border: none;
  padding: 0;
  cursor: pointer;
  pointer-events: auto;
  background-color: transparent;

  &:disabled {
    pointer-events: none;
  }

  &:focus-visible {
    outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
    border-radius: var(--qds-focus-border-radius);
    outline-offset: var(--qds-focus-border-offset);
  }
}

.qds-target-icon {
  color: var(--qds-theme-control-text-standard);
}

.qds-icon,
.qds-target-icon {
  flex-shrink: 0;
}

.qds-chevron {
  transform: rotate(180deg);
}

.qds-tab,
.qds-closable-wrapper {
  position: relative;
}

.qds-target,
.qds-closable-wrapper,
.qds-tab,
.qds-closable-tab,
.qds-texts {
  display: flex;
}

.qds-texts {
  flex: 1 0;
  flex-direction: column;
}

.qds-icon,
.qds-text {
  color: var(--qds-theme-title);
}

.qds-subtitle {
  color: var(--qds-theme-subtitle);
  font: var(--qds-control-tab-standard-subtitle);
}

.qds-indicator {
  position: absolute;
  inset-block-start: 0;
  inset-inline-end: 0;
  transform: translate(50%, -50%);
}

[data-size='small'] {
  &.qds-tab,
  &.qds-closable-wrapper {
    min-height: var(--qds-control-tab-small-height);
    gap: var(--qds-control-tab-small-gap-internal);
    padding-inline: var(--qds-control-tab-small-padding-horizontal);
  }

  &.qds-collapse {
    min-width: var(--qds-control-tab-small-height);
  }

  &.qds-closable-tab {
    gap: var(--qds-control-tab-small-gap-internal);
  }

  & > .qds-icon {
    font-size: var(--qds-control-tab-small-icon-size);
  }

  & > .qds-texts {
    gap: var(--qds-control-tab-small-titles-gap);
  }

  & .qds-text {
    font: var(--qds-control-tab-small-title);
  }

  & .qds-subtitle {
    font: var(--qds-control-tab-small-subtitle);
  }

  &.qds-target-icon {
    --qds-experimental-icon-click-target-size: var(--qds-control-small-height);

    height: var(--qds-control-small-icon-size);
    width: var(--qds-control-small-icon-size);
  }
}

[data-size='standard'] {
  &.qds-tab,
  &.qds-closable-wrapper {
    min-height: var(--qds-control-tab-standard-height);
    gap: var(--qds-control-tab-standard-gap-internal);
    padding-inline: var(--qds-control-tab-standard-padding-horizontal);
  }

  &.qds-collapse {
    min-width: var(--qds-control-tab-standard-height);
  }

  &.qds-closable-tab {
    gap: var(--qds-control-tab-standard-gap-internal);
  }

  & > .qds-icon {
    font-size: var(--qds-control-tab-standard-icon-size);
  }

  & > .qds-texts {
    gap: var(--qds-control-tab-standard-titles-gap);
  }

  & .qds-text {
    font: var(--qds-control-tab-standard-title);
  }

  & .qds-subtitle {
    font: var(--qds-control-tab-standard-subtitle);
  }

  &.qds-target-icon {
    --qds-experimental-icon-click-target-size: var(
      --qds-control-standard-height
    );

    height: var(--qds-control-standard-icon-size);
    width: var(--qds-control-standard-icon-size);
  }
}

[data-size='large'] {
  &.qds-tab,
  &.qds-closable-wrapper {
    min-height: var(--qds-control-tab-large-height);
    gap: var(--qds-control-tab-large-gap-internal);
    padding-inline: var(--qds-control-tab-large-padding-horizontal);
  }

  &.qds-collapse {
    min-width: var(--qds-control-tab-large-height);
  }

  &.qds-closable-tab {
    gap: var(--qds-control-tab-large-gap-internal);
  }

  & > .qds-icon {
    font-size: var(--qds-control-tab-large-icon-size);
  }

  & > .qds-texts {
    gap: var(--qds-control-tab-large-titles-gap);
  }

  & .qds-text {
    font: var(--qds-control-tab-large-title);
  }

  & .qds-subtext {
    font: var(--qds-control-tab-large-subtitle);
  }

  &.qds-target-icon {
    --qds-experimental-icon-click-target-size: var(--qds-control-large-height);

    height: var(--qds-control-large-icon-size);
    width: var(--qds-control-large-icon-size);
  }
}

[data-importance='emphasized'] {
  &.qds-selected {
    &.qds-tab,
    &.qds-closable-wrapper {
      background-color: var(--qds-theme-control-background);
    }
  }

  &[data-size='standard'] .qds-text {
    font: var(--qds-control-tab-standard-title-emphasized);
  }

  &[data-size='small'] .qds-text {
    font: var(--qds-control-tab-small-title-emphasized);
  }

  &[data-size='large'] .qds-text {
    font: var(--qds-control-tab-large-title-emphasized);
  }

  & .qds-icon {
    color: var(--qds-theme-signature-color-default);
  }
}

[data-importance='standard'] {
  .qds-selected .qds-icon {
    color: var(--qds-theme-signature-color-default);
  }
}

[data-importance='subdued'],
[data-importance='standard'],
[data-importance='emphasized'] {
  &.qds-selected,
  &.qds-closable-wrapper,
  .qds-tab:hover {
    &::after {
      border-radius: var(--qds-signature-line-connection-cap-radius);
      content: '';
      display: block;
      position: absolute;
    }

    &.qds-indicator-inline-end::after,
    &.qds-indicator-inline-start::after {
      width: var(--qds-signature-line-connection-width);
      height: 100%;
    }

    &.qds-indicator-inline-end::after {
      inset-inline-end: 0;
    }

    &.qds-indicator-inline-start::after {
      inset-inline-start: 0;
    }

    &.qds-indicator-block-end::after,
    &.qds-indicator-block-start::after {
      width: 100%;
      height: var(--qds-signature-line-connection-width);
    }

    &.qds-indicator-block-end::after {
      bottom: 0;
    }

    &.qds-indicator-block-start::after {
      top: 0;
    }
  }
}

[data-importance='subdued'],
[data-importance='standard'] {
  &.qds-selected {
    &[data-size='standard'] .qds-text {
      font: var(--qds-control-tab-standard-title-emphasized);
    }

    &[data-size='small'] .qds-text {
      font: var(--qds-control-tab-small-title-emphasized);
    }

    &[data-size='large'] .qds-text {
      font: var(--qds-control-tab-large-title-emphasized);
    }
  }
}

[data-importance='standard'],
[data-importance='emphasized'] {
  &:hover {
    &::after {
      background-color: var(--qds-theme-signature-color-hover);
    }
  }

  &:active {
    &::after {
      background-color: var(--qds-theme-signature-color-pressed);
    }
  }

  &.qds-selected {
    &::after {
      background-color: var(--qds-theme-signature-color-default);
    }
  }
}

[data-importance='subdued'] {
  &.qds-selected,
  :active {
    &::after {
      background-color: var(--qds-theme-link-subdued-pressed);
    }
  }

  &:hover {
    &::after {
      background-color: var(--qds-theme-link-subdued-hover);
    }
  }
}

.qds-tab {
  &.qds-icon-only {
    padding-inline: 0;
  }

  &:focus-visible {
    outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
    border-radius: var(--qds-focus-border-radius);
    outline-offset: calc(
      -1 * var(--qds-focus-border-width) - var(--qds-focus-border-offset)
    );
  }
}

.qds-closable-tab {
  width: 100%;

  &:focus-visible {
    outline: var(--qds-theme-focus-border) solid var(--qds-focus-border-width);
    border-radius: var(--qds-focus-border-radius);
  }
}

.qds-closable-wrapper {
  align-items: center;
  justify-content: center;
}

.qds-tab,
.qds-closable-tab {
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  cursor: pointer;
  text-align: initial;

  &:any-link {
    text-decoration: none;
  }
}

.qds-hidden {
  display: none;
}
